/**
 * Dashboard Page - Trade Loop v2.0
 *
 * Migrated from v1.0 with cookie-based authentication and React Router DOM
 * Uses AppLayout component with Header + BottomNavigation
 *
 * Features:
 * - Active Referral Count
 * - Revenue this month tracker
 * - Partner Connection Count tracker
 * - Subscription Plan Tag (Starter, Core, Grow)
 * - Plan upgrade prompts
 * - Real-time updates via Supabase Realtime
 */

import { Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { useRealtimeDashboard } from "@/hooks/useRealtimeDashboard";
import { PartnerNetworkProgress } from "@/components/PartnerNetworkProgress";
import { NeedsYourAttentionCard } from "@/components/NeedsYourAttentionCard";
import GettingStartedPanel from "@/components/getting-started/GettingStartedPanel";
import { Users, Target, Crown, ArrowRight, Sparkles, TrendingUp, ExternalLink, Building2, ChevronRight, Pencil } from "lucide-react";
import { DashboardStats } from "@/types/api";

// Subscription plan display names and colors
const planConfig: Record<string, { name: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  starter: {
    name: "Starter",
    color: "text-gray-700",
    bgColor: "bg-gray-100",
    icon: <Sparkles className="w-3 h-3" />
  },
  pro: {
    name: "Pro",
    color: "text-blue-700",
    bgColor: "bg-blue-100",
    icon: <Crown className="w-3 h-3" />
  },
  grow: {
    name: "Grow",
    color: "text-purple-700",
    bgColor: "bg-purple-100",
    icon: <Crown className="w-3 h-3" />
  }
};

export default function DashboardPage() {
  const { user, sessionReady } = useAuth();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();


  // Redirect team members (technician/member) to organization page
  // Their home page is the organization page, not the dashboard
  useEffect(() => {
    if (user?.organizationMembership) {
      const role = user.organizationMembership.role;
      if (role === 'technician' || role === 'member') {
        navigate('/organization', { replace: true });
      }
    }
  }, [user, navigate]);

  // Subscribe to Supabase Realtime for live dashboard updates
  // CRITICAL: Wait for sessionReady to ensure Realtime auth is set before subscribing
  // Without this, the WebSocket connects as anonymous and RLS blocks INSERT events
  useRealtimeDashboard({
    userId: user?.id,
    enabled: !!user && sessionReady,
  });

  // Diagnostic: Log auth state for debugging
  useEffect(() => {
    console.log('[DashboardPage] Auth state:', {
      hasUser: !!user,
      userId: user?.id,
      sessionReady,
      realtimeEnabled: !!user && sessionReady
    });
  }, [user, sessionReady]);


  // Fetch dashboard stats from API
  // Realtime handles ALL updates; no polling needed (prevents UI jumps)
  const {
    data: statsResponse,
    isPending: statsLoading,
    isFetching: statsFetching
  } = useQuery<DashboardStats>({
    queryKey: ["/api/workflows/dashboard"],
    queryFn: () => api.get<DashboardStats>("/api/workflows/dashboard"),
    enabled: !!user,
    staleTime: 30 * 1000, // Data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnMount: true, // Always refetch when navigating to dashboard
    refetchOnWindowFocus: false, // Realtime handles focus updates
    refetchOnReconnect: false, // Realtime handles reconnection
    // No refetchInterval - Realtime provides instant updates without polling
  });

  // Fetch organization data (for Grow plan users)
  const {
    data: organizationResponse,
  } = useQuery({
    queryKey: ["/api/workflows/organization/my-organization"],
    queryFn: () => api.get("/api/workflows/organization/my-organization"),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // Data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: false, // Don't retry if user doesn't have an organization
  });

  // Dashboard endpoint returns data directly (not wrapped in ApiResponse)
  const stats = statsResponse;

  // Extract organization data (for Grow plan users)
  const organizationData = organizationResponse?.data?.data || organizationResponse?.data;
  const organization = organizationData?.organization;
  const orgMembers = organizationData?.members || [];
  const isGrowPlan = stats?.subscriptionPlan === 'grow';
  const hasOrganization = !!organization;

  // Only show skeletons on INITIAL load (no data yet), not during background refetches
  const isInitialLoading = statsLoading && !statsResponse;

  // Track if data is being refreshed in the background for subtle indicators
  const isRefreshingData = statsFetching && !statsLoading;


  // Get subscription plan info
  const subscriptionPlan = stats?.subscriptionPlan || 'starter';
  const planInfo = planConfig[subscriptionPlan] || planConfig.starter;
  const isStarterPlan = subscriptionPlan === 'starter';

  // Pull-to-refresh functionality - pass to AppLayout
  const handleRefresh = async () => {
    // Invalidate and refetch all dashboard data
    await queryClient.invalidateQueries({ queryKey: ["/api/workflows/dashboard"] });

    // Force refetch with fresh data
    await queryClient.refetchQueries({ queryKey: ["/api/workflows/dashboard"] });
  };

  return (
    <AppLayout activeTab="dashboard" title="Dashboard" onRefresh={handleRefresh}>
        {/* Main Content */}
        <main>
          <div className="px-4 pb-10 md:pb-0 space-y-6 max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
            {/* Welcome Section with Plan Badge */}
            <div className="animate-slide-up">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <h2 className="text-2xl font-semibold text-gray-800 tracking-tight leading-snug">
                    Welcome back{user?.firstName ? `, ${user.firstName}` : ''}
                  </h2>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <p className="text-gray-500">Here's your network overview</p>
                  {!isInitialLoading && (
                    <Badge className={`${planInfo.bgColor} ${planInfo.color} border-0 flex items-center gap-1`}>
                      {planInfo.icon}
                      {planInfo.name}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Getting Started Panel - shown for onboarded users until they
                explicitly dismiss it (per-user localStorage). */}
            <GettingStartedPanel />

            {/* Stats Cards - Row 1: Active Referrals and This Month's Revenue */}
            <div className="grid grid-cols-2 gap-4 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              {/* Active Referrals Card */}
              <Card
                className="shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 bg-gradient-to-r from-blue-500 to-indigo-500 text-white group hover:scale-[1.02] cursor-pointer"
                onClick={() => navigate('/referrals')}
              >
                <CardContent className="p-5 flex flex-col items-center justify-center text-center">
                  <p className="text-sm font-medium text-blue-100 mb-1">Active Referrals</p>
                  <div className="text-2xl font-bold text-white tracking-tight transition-opacity duration-300" style={{ opacity: isRefreshingData ? 0.6 : 1 }}>
                    {isInitialLoading ? (
                      <Skeleton className="h-8 w-12 bg-white/20" />
                    ) : (
                      stats?.activeReferrals || "0"
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Revenue This Month Card */}
              <Card className="shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 bg-gradient-to-r from-emerald-500 to-green-500 text-white group hover:scale-[1.02]">
                <CardContent className="p-5 flex flex-col items-center justify-center text-center">
                  <p className="text-sm font-medium text-emerald-100 mb-1">Revenue This Month</p>
                  <div className="text-2xl font-bold text-white tracking-tight transition-opacity duration-300" style={{ opacity: isRefreshingData ? 0.6 : 1 }}>
                    {isInitialLoading ? (
                      <Skeleton className="h-8 w-16 bg-white/20" />
                    ) : (
                      `$${(stats?.revenueThisMonth || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Needs Your Attention Card */}
            <div className="animate-slide-up" style={{ animationDelay: '0.15s' }}>
              <NeedsYourAttentionCard />
            </div>

            {/* Partner Network Health */}
            <div className="animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <PartnerNetworkProgress
                userId={user?.id}
                compact={false}
                showCategoryDetails={true}
                partnerConnections={stats?.partnerConnections}
              />
            </div>

            {/* Organization Card - For users with an organization (any plan) */}
            {!isInitialLoading && hasOrganization && (
              <div className="animate-slide-up" style={{ animationDelay: '0.275s' }}>
                <Card className={`shadow-md transition-all duration-300 border ${
                  organization.status === 'paused'
                    ? 'border-gray-300 bg-gradient-to-r from-gray-50 to-gray-100'
                    : 'border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50 hover:shadow-lg hover:scale-[1.01]'
                }`}>
                  <CardContent className="p-5">
                    {organization.status === 'paused' ? (
                      // Paused state - not clickable, button on right
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                            <Building2 className="w-6 h-6 text-gray-500" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-gray-600">{organization.name}</h3>
                              <Badge className="bg-red-100 text-red-700 border-0 text-xs px-1.5 py-0">
                                Paused
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Users className="w-3.5 h-3.5" />
                                {orgMembers.length} member{orgMembers.length !== 1 ? 's' : ''}
                              </span>
                              {user?.id === organization.owner_id && (
                                <Badge className="bg-amber-100 text-amber-700 border-0 text-xs px-1.5 py-0">
                                  <Crown className="w-3 h-3 mr-0.5" />
                                  Owner
                                </Badge>
                              )}
                            </div>
                            {organization.paused_reason && (
                              <p className="text-xs text-gray-500 mt-1 max-w-xs">{organization.paused_reason}</p>
                            )}
                          </div>
                        </div>
                        <Link to="/plans">
                          <Button size="sm" className="bg-purple-500 hover:bg-purple-600 text-white rounded-xl whitespace-nowrap">
                            <Crown className="w-4 h-4 mr-1" />
                            Upgrade to Grow
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      // Active state - entire card is clickable
                      <Link to="/organization" className="block group">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 flex-1">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-teal-100 to-teal-200 group-hover:from-teal-200 group-hover:to-teal-300 transition-all">
                              <Building2 className="w-6 h-6 text-teal-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-gray-800">{organization.name}</h3>
                              </div>
                              <div className="flex items-center gap-3 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Users className="w-3.5 h-3.5" />
                                  {orgMembers.length} member{orgMembers.length !== 1 ? 's' : ''}
                                </span>
                                {user?.id === organization.owner_id && (
                                  <Badge className="bg-amber-100 text-amber-700 border-0 text-xs px-1.5 py-0">
                                    <Crown className="w-3 h-3 mr-0.5" />
                                    Owner
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 transition-all text-teal-400 group-hover:text-teal-600 group-hover:translate-x-1" />
                        </div>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Upgrade Prompt for Starter Plans */}
            {!isInitialLoading && isStarterPlan && (
              <Card className="shadow-md border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 animate-slide-up" style={{ animationDelay: '0.325s' }}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                        <Crown className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Unlock More Features</p>
                        <p className="text-sm text-gray-500">Upgrade to Pro for more features</p>
                      </div>
                    </div>
                    <Link to="/plans">
                      <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl">
                        <span>Upgrade</span>
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </main>
    </AppLayout>
  );
}
